import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Phone } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useScrollTo, EASE, openSchedule } from "./Reveal";

const LINKS = [
  { label: "Solutions", href: "#services" },
  { label: "Approach", href: "#approach" },
  { label: "Calculator", href: "#calculator" },
  { label: "About", href: "#about" },
  { label: "Brands", href: "/our-brands" },
  { label: "Blog", href: "/blog" },
  { label: "FAQ", href: "/faq" },
  { label: "Contact", href: "#contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const scrollTo = useScrollTo();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    setTimeout(() => scrollTo(href), open ? 350 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
        className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-500 ${
          scrolled ? "bg-white/90 backdrop-blur-xl border-ink/10" : "bg-white/70 backdrop-blur-md border-transparent"
        }`}
        data-testid="main-nav"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-16 lg:h-20 flex items-center justify-between">
          <button onClick={() => go("#top")} className="flex items-center" aria-label="Skope Kitchens home" data-testid="nav-logo">
            <img src="/assets/skope-logo-full.png" alt="Skope Kitchens" className="h-6 lg:h-7 w-auto" />
          </button>

          <nav className="hidden lg:flex items-center gap-10" aria-label="Primary">
            {LINKS.map((l) => (
              <button
                key={l.label}
                onClick={() => go(l.href)}
                className="font-mono text-[11px] tracking-[0.25em] uppercase text-ink/60 hover:text-skope transition-colors duration-300"
                data-testid={`nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <a
              href="tel:+918618689724"
              className="hidden xl:flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] text-ink/60 hover:text-skope transition-colors duration-300"
              data-testid="nav-phone"
            >
              <Phone size={13} /> +91 8618689724
            </a>
            <button
              onClick={() => openSchedule()}
              className="hidden lg:flex items-center gap-2 bg-skope text-ink font-mono text-[11px] tracking-[0.2em] uppercase px-5 py-2.5 hover:bg-ink hover:text-white transition-colors duration-300"
              data-testid="nav-cta-schedule"
            >
              Schedule a Call
              <ArrowUpRight size={14} />
            </button>
            <button
              className="lg:hidden text-ink p-2"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              data-testid="nav-hamburger"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] bg-white/95 backdrop-blur-2xl flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-ink/10">
              <img src="/assets/skope-logo-full.png" alt="Skope Kitchens" className="h-6 w-auto" />
              <button onClick={() => setOpen(false)} aria-label="Close menu" className="text-ink p-2" data-testid="mobile-menu-close">
                <X size={24} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2" aria-label="Mobile">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.label}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i + 0.1, duration: 0.6, ease: EASE }}
                  onClick={() => go(l.href)}
                  className="text-left font-display text-4xl font-semibold text-ink/85 hover:text-skope transition-colors py-3 border-b border-ink/5"
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                >
                  <span className="font-mono text-xs text-skope mr-4 align-middle">0{i + 1}</span>
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.6, ease: EASE }}
                onClick={() => { setOpen(false); openSchedule(); }}
                className="mt-10 flex items-center justify-center gap-3 bg-skope text-ink font-mono text-xs tracking-[0.25em] uppercase px-6 py-4"
                data-testid="mobile-cta-schedule"
              >
                Schedule a Call <ArrowUpRight size={16} />
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
